import React, { useState, useEffect, useCallback } from 'react';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function VentasAliaddo() {
  const hoy = new Date();
  const [inicio, setInicio]       = useState(format(startOfMonth(hoy), 'yyyy-MM-dd'));
  const [fin, setFin]             = useState(format(hoy, 'yyyy-MM-dd'));
  const [bodegaId, setBodegaId]   = useState('');
  const [bodegas, setBodegas]     = useState([]);
  const [resumen, setResumen]     = useState([]);
  const [stats, setStats]         = useState(null);
  const [cargando, setCargando]   = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [archivo, setArchivo]     = useState(null);
  const [vista, setVista]         = useState('resumen'); // 'resumen' | 'diario' | 'salidas' | 'recetas'
  const [fechaSalidas, setFechaSalidas] = useState(format(hoy, 'yyyy-MM-dd'));
  const [salidas, setSalidas]     = useState(null);
  const [cargandoSalidas, setCargandoSalidas] = useState(false);

  // Recetas Aliaddo
  const [recetas, setRecetas]           = useState([]);
  const [productosAliaddo, setProductosAliaddo] = useState([]);
  const [itemsInv, setItemsInv]         = useState([]);
  const [cargandoRecetas, setCargandoRecetas] = useState(false);
  const [formReceta, setFormReceta]     = useState({ nombre_aliaddo: '', item_id: '', cantidad: 1 });
  const [guardandoReceta, setGuardandoReceta] = useState(false);
  const [filtroReceta, setFiltroReceta] = useState('');

  useEffect(() => {
    API.get('/bodegas').then(r => setBodegas(r.data)).catch(() => {});
  }, []);

  const cargarSalidas = (fecha, bodega) => {
    if (!bodega) return;
    setCargandoSalidas(true);
    API.get('/ventas/salidas', { params: { bodega_id: bodega, fecha: fecha || fechaSalidas } })
      .then(r => setSalidas(r.data))
      .catch(() => toast.error('Error cargando salidas'))
      .finally(() => setCargandoSalidas(false));
  };

  useEffect(() => {
    if (vista === 'salidas' && bodegaId) cargarSalidas(fechaSalidas, bodegaId);
  }, [vista, fechaSalidas, bodegaId]);

  const cargarRecetas = () => {
    setCargandoRecetas(true);
    Promise.all([
      API.get('/aliaddo/recetas'),
      API.get('/aliaddo/productos'),
      API.get('/inventario/items', { params: { tipo: 'diario' } }),
    ]).then(([r, p, i]) => {
      setRecetas(r.data);
      setProductosAliaddo(p.data);
      setItemsInv(i.data);
    }).catch(() => toast.error('Error cargando recetas'))
    .finally(() => setCargandoRecetas(false));
  };

  useEffect(() => {
    if (vista === 'recetas') cargarRecetas();
  }, [vista]);

  const handleGuardarReceta = async (datos) => {
    await API.post('/aliaddo/recetas', datos);
    cargarRecetas();
  };

  const handleEliminarReceta = async (id) => {
    if (!window.confirm('¿Eliminar esta receta?')) return;
    try {
      await API.delete(`/aliaddo/recetas/${id}`);
      toast.success('Receta eliminada');
      cargarRecetas();
    } catch(e) { toast.error('Error al eliminar'); }
  };

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      API.get('/ventas/resumen', { params: { inicio, fin, bodega_id: bodegaId || null } }),
      API.get('/ventas/stats',   { params: { inicio, fin } }),
    ]).then(([r, s]) => {
      setResumen(r.data);
      setStats(s.data);
    }).catch(() => toast.error('Error cargando ventas'))
    .finally(() => setCargando(false));
  }, [inicio, fin, bodegaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleImportar = async () => {
    if (!archivo) { toast.error('Selecciona un archivo Excel'); return; }
    setImportando(true);
    setResultado(null);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      const r = await API.post('/ventas/importar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000  // 2 minutos para archivos grandes
      });
      setResultado(r.data);
      toast.success(r.data.mensaje);
      setArchivo(null);
      document.getElementById('input-excel').value = '';
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al importar');
    } finally { setImportando(false); }
  };

  // Agrupar resumen por día
  const porDia = {};
  resumen.forEach(r => {
    const dia = r.dia?.slice(0, 10);
    if (!porDia[dia]) porDia[dia] = { dia, sucursales: [], total: 0, facturas: 0 };
    porDia[dia].sucursales.push(r);
    porDia[dia].total += parseFloat(r.total_ventas) || 0;
    porDia[dia].facturas += parseInt(r.num_facturas) || 0;
  });
  const diasOrdenados = Object.values(porDia).sort((a, b) => b.dia.localeCompare(a.dia));

  // Resumen por sucursal
  const porSucursal = {};
  resumen.forEach(r => {
    const s = r.sucursal;
    if (!porSucursal[s]) porSucursal[s] = { sucursal: s, total: 0, facturas: 0, efectivo: 0, tarjeta: 0 };
    porSucursal[s].total    += parseFloat(r.total_ventas) || 0;
    porSucursal[s].facturas += parseInt(r.num_facturas) || 0;
    porSucursal[s].efectivo += parseFloat(r.efectivo) || 0;
    porSucursal[s].tarjeta  += parseFloat(r.tarjeta) || 0;
  });

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>📊 Ventas Aliaddo</h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Importa y visualiza las ventas de todos los puntos</p>
        </div>
      </div>

      {/* Panel de importación */}
      <div style={{ background: 'white', borderRadius: 14, padding: '18px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 22 }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14 }}>📥 Importar reporte de Aliaddo</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 5 }}>
              Archivo Excel (.xlsx) exportado desde Aliaddo
            </div>
            <input
              id="input-excel"
              type="file"
              accept=".xlsx,.xls"
              onChange={e => setArchivo(e.target.files[0])}
              style={{ width: '100%', padding: '8px 12px', border: '2px dashed #E5E7EB', borderRadius: 8, fontSize: 13, cursor: 'pointer', boxSizing: 'border-box' }}
            />
          </div>
          <button
            onClick={handleImportar}
            disabled={importando || !archivo}
            style={{ padding: '10px 22px', border: 'none', borderRadius: 8, background: archivo ? 'linear-gradient(135deg,#1a1a2e,#16213e)' : '#E5E7EB', color: archivo ? 'white' : '#9CA3AF', cursor: archivo ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            {importando ? '⏳ Importando...' : '📤 Importar'}
          </button>
        </div>

        {/* Resultado importación */}
        {resultado && (
          <div style={{ marginTop: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#065F46', fontWeight: 700 }}>✅ {resultado.mensaje}</span>
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#374151' }}>
              <span>✅ <strong>{resultado.insertadas}</strong> nuevas</span>
              <span>⏭️ <strong>{resultado.omitidas}</strong> ya existían</span>
              {resultado.errores > 0 && <span style={{ color: '#EF4444' }}>❌ <strong>{resultado.errores}</strong> errores</span>}
            </div>
          </div>
        )}

        <div style={{ marginTop: 10, fontSize: 12, color: '#9CA3AF' }}>
          💡 El archivo puede contener todo el mes — el sistema detecta automáticamente las nuevas facturas y no duplica las que ya existen.
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Desde</div>
          <input type="date" value={inicio} onChange={e => setInicio(e.target.value)}
            style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Hasta</div>
          <input type="date" value={fin} onChange={e => setFin(e.target.value)}
            style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Punto de venta</div>
          <select value={bodegaId} onChange={e => setBodegaId(e.target.value)}
            style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' }}>
            <option value="">Todos</option>
            {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
        {/* Accesos rápidos */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: 'Hoy', i: format(hoy,'yyyy-MM-dd'), f: format(hoy,'yyyy-MM-dd') },
            { label: 'Este mes', i: format(startOfMonth(hoy),'yyyy-MM-dd'), f: format(hoy,'yyyy-MM-dd') },
            { label: 'Mes anterior', i: format(startOfMonth(subMonths(hoy,1)),'yyyy-MM-dd'), f: format(endOfMonth(subMonths(hoy,1)),'yyyy-MM-dd') },
          ].map(a => (
            <button key={a.label} onClick={() => { setInicio(a.i); setFin(a.f); }}
              style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats globales */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 22 }}>
          {[
            { label: 'Total ventas', valor: COP(stats.total_ventas), icon: '💰', color: '#10B981' },
            { label: 'Facturas', valor: parseInt(stats.total_facturas).toLocaleString(), icon: '🧾', color: '#3B82F6' },
            { label: 'Ticket promedio', valor: COP(stats.ticket_promedio), icon: '📊', color: '#8B5CF6' },
            { label: 'Efectivo', valor: COP(stats.efectivo), icon: '💵', color: '#F59E0B' },
            { label: 'Tarjeta', valor: COP(stats.tarjeta), icon: '💳', color: '#6366F1' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 18, color: s.color }}>{s.valor}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs vista */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'white', borderRadius: 10, padding: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', width: 'fit-content' }}>
        {[{ id: 'resumen', label: '🏪 Por sucursal' }, { id: 'diario', label: '📅 Por día' }, { id: 'salidas', label: '📦 Salidas de inventario' }, { id: 'recetas', label: '📋 Recetas' }].map(v => (
          <button key={v.id} onClick={() => setVista(v.id)} style={{ padding: '7px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: vista === v.id ? 700 : 500, background: vista === v.id ? '#1a1a2e' : 'transparent', color: vista === v.id ? 'white' : '#6B7280' }}>
            {v.label}
          </button>
        ))}
      </div>

      {cargando ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Cargando...</div>
      ) : resumen.length === 0 ? (
        <div style={{ background: 'white', borderRadius: 14, padding: 40, textAlign: 'center', color: '#9CA3AF', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Sin ventas importadas en este período</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Importa el reporte Excel de Aliaddo para ver los datos</div>
        </div>
      ) : vista === 'resumen' ? (
        /* Vista por sucursal */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.values(porSucursal).sort((a,b) => b.total - a.total).map(s => {
            const pctEfectivo = s.total > 0 ? (s.efectivo / s.total * 100) : 0;
            const pctTarjeta  = s.total > 0 ? (s.tarjeta  / s.total * 100) : 0;
            return (
              <div key={s.sucursal} style={{ background: 'white', borderRadius: 14, padding: '18px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>🏪 {s.sucursal}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{s.facturas} facturas</div>
                  </div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 22, color: '#10B981' }}>{COP(s.total)}</div>
                </div>
                {/* Barra medios de pago */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#F3F4F6' }}>
                    <div style={{ width: `${pctEfectivo}%`, background: '#10B981' }} />
                    <div style={{ width: `${pctTarjeta}%`,  background: '#3B82F6' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12 }}>
                    <span style={{ color: '#10B981', fontWeight: 700 }}>💵 Efectivo: {COP(s.efectivo)} ({pctEfectivo.toFixed(0)}%)</span>
                    <span style={{ color: '#3B82F6', fontWeight: 700 }}>💳 Tarjeta: {COP(s.tarjeta)} ({pctTarjeta.toFixed(0)}%)</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : vista === 'diario' ? (
        /* Vista por día */
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#1a1a2e' }}>
                  {['Día','Sucursal','Facturas','Efectivo','Tarjeta','Total'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Día' || h === 'Sucursal' ? 'left' : 'right', color: 'white', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {diasOrdenados.map((dia, di) => (
                  dia.sucursales.map((s, si) => (
                    <tr key={`${dia.dia}-${s.sucursal}`} style={{ borderTop: '1px solid #F3F4F6', background: di % 2 === 0 ? 'white' : '#FAFAFA' }}>
                      {si === 0 ? (
                        <td rowSpan={dia.sucursales.length} style={{ padding: '10px 14px', fontWeight: 700, verticalAlign: 'top', borderRight: '1px solid #F3F4F6' }}>
                          <div>{format(new Date(dia.dia + 'T12:00'), "d 'de' MMM", { locale: es })}</div>
                          <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 400 }}>{format(new Date(dia.dia + 'T12:00'), 'EEEE', { locale: es })}</div>
                          <div style={{ fontFamily: 'monospace', fontWeight: 800, color: '#10B981', marginTop: 4 }}>{COP(dia.total)}</div>
                        </td>
                      ) : null}
                      <td style={{ padding: '10px 14px' }}>{s.sucursal}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: '#6B7280' }}>{s.num_facturas}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#10B981', fontWeight: 600 }}>{COP(s.efectivo)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#3B82F6', fontWeight: 600 }}>{COP(s.tarjeta)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800 }}>{COP(s.total_ventas)}</td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Recetas Aliaddo */}
      {vista === 'recetas' && (
        <RecetasAliaddo
          productosAliaddo={productosAliaddo}
          itemsInv={itemsInv}
          recetas={recetas}
          cargandoRecetas={cargandoRecetas}
          onGuardar={handleGuardarReceta}
          onEliminar={handleEliminarReceta}
          filtroReceta={filtroReceta}
          setFiltroReceta={setFiltroReceta}
          guardandoReceta={guardandoReceta}
          formReceta={formReceta}
          setFormReceta={setFormReceta}
        />
      )}

      {/* Salidas de inventario Aliaddo */}
      {vista === 'salidas' && (
        <>
          <div style={{ display:'flex', gap:12, marginBottom:18, alignItems:'flex-end', flexWrap:'wrap' }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Fecha</div>
              <input type="date" value={fechaSalidas}
                onChange={e => setFechaSalidas(e.target.value)}
                max={format(hoy,'yyyy-MM-dd')}
                style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none' }} />
            </div>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Punto de venta</div>
              <select value={bodegaId} onChange={e => setBodegaId(e.target.value)}
                style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'white' }}>
                <option value="">Seleccionar PDV...</option>
                {bodegas.filter(b => b.tipo === 'punto_venta').map(b => (
                  <option key={b.id} value={b.id}>{b.nombre}</option>
                ))}
              </select>
            </div>
            {cargandoSalidas && <span style={{ fontSize:13, color:'#6B7280', padding:'8px 0' }}>⏳ Calculando...</span>}
          </div>
          {!bodegaId && (
            <div style={{ background:'white', borderRadius:14, padding:32, textAlign:'center', color:'#9CA3AF', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              Selecciona un punto de venta para ver las salidas
            </div>
          )}
          {bodegaId && salidas && salidas.cuadre?.length === 0 && (
            <div style={{ background:'white', borderRadius:14, padding:32, textAlign:'center', color:'#9CA3AF', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              Sin ventas de Aliaddo para esta fecha en este PDV.
            </div>
          )}
          {bodegaId && salidas && salidas.cuadre?.length > 0 && (
            <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ background:'#1a1a2e', padding:'12px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ color:'white', fontWeight:800, fontSize:14 }}>
                  📦 Salidas Aliaddo — {format(new Date(fechaSalidas+'T12:00'),"d 'de' MMMM yyyy",{locale:es})}
                </div>
                <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12 }}>
                  {salidas.cuadre.filter(c => c.consumo_teorico > 0).length} materias primas · {salidas.total_ventas || 0} unds vendidas
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#F9FAFB' }}>
                      <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Materia prima</th>
                      <th style={{ padding:'10px 14px', textAlign:'right', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Unidad</th>
                      <th style={{ padding:'10px 14px', textAlign:'right', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Consumo teórico</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salidas.cuadre
                      .filter(c => c.consumo_teorico > 0)
                      .sort((a,b) => b.consumo_teorico - a.consumo_teorico)
                      .map((c,i) => (
                      <tr key={c.item_id} style={{ borderTop:'1px solid #F3F4F6', background:i%2===0?'white':'#FAFAFA' }}>
                        <td style={{ padding:'10px 14px', fontWeight:600, color:'#111827' }}>{c.nombre}</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', color:'#6B7280', fontSize:12 }}>{c.unidad || '—'}</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', fontFamily:'monospace', fontWeight:800, color:'#111827', fontSize:14 }}>
                          {Math.round(c.consumo_teorico)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// COMPONENTE: RECETAS ALIADDO
// Lista de productos Aliaddo → click → modal con múltiples items
// ─────────────────────────────────────────
function RecetasAliaddo({ productosAliaddo, itemsInv, recetas, cargandoRecetas, onGuardar, onEliminar, filtroReceta, setFiltroReceta, guardandoReceta, formReceta, setFormReceta }) {
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [itemsReceta, setItemsReceta] = useState([]); // [{item_id, cantidad}]

  // Recetas del producto seleccionado
  const recetasDelProducto = productoSeleccionado
    ? recetas.filter(r => r.nombre_aliaddo === productoSeleccionado)
    : [];

  // IDs ya asignados al producto seleccionado
  const idsAsignados = new Set(recetasDelProducto.map(r => r.item_id));

  // Productos únicos con conteo de items en receta
  const productosConReceta = new Set(recetas.map(r => r.nombre_aliaddo));

  const abrirProducto = (nombre) => {
    setProductoSeleccionado(nombre);
    setItemsReceta([{ item_id: '', cantidad: 1 }]);
  };

  const handleAgregarFila = () => {
    setItemsReceta(prev => [...prev, { item_id: '', cantidad: 1 }]);
  };

  const handleCambiarFila = (idx, campo, valor) => {
    setItemsReceta(prev => prev.map((f, i) => i === idx ? { ...f, [campo]: valor } : f));
  };

  const handleEliminarFila = (idx) => {
    setItemsReceta(prev => prev.filter((_, i) => i !== idx));
  };

  const [guardando, setGuardando] = useState(false);

  const handleGuardarItems = async () => {
    const validos = itemsReceta.filter(f => f.item_id && parseFloat(f.cantidad) > 0);
    if (!validos.length) { toast.error('Agrega al menos un item con cantidad'); return; }
    setGuardando(true);
    try {
      for (const f of validos) {
        await onGuardar({ nombre_aliaddo: productoSeleccionado, item_id: parseInt(f.item_id), cantidad: parseFloat(f.cantidad) });
      }
      setItemsReceta([{ item_id: '', cantidad: 1 }]);
      toast.success('Receta guardada');
    } catch(e) {
      toast.error('Error al guardar receta');
    } finally {
      setGuardando(false);
    }
  };

  const sinFiltro = !filtroReceta;
  const productosFiltrados = productosAliaddo.filter(p =>
    sinFiltro || p.nombre.toLowerCase().includes(filtroReceta.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

      {/* Panel izquierdo: lista de productos */}
      <div style={{ width: 340, flexShrink: 0, background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ background: '#1a1a2e', padding: '12px 16px' }}>
          <div style={{ color: 'white', fontWeight: 800, fontSize: 14, marginBottom: 8 }}>
            📦 Productos Aliaddo
          </div>
          <input
            placeholder="Filtrar productos..."
            value={filtroReceta}
            onChange={e => setFiltroReceta(e.target.value)}
            style={{ width: '100%', padding: '7px 12px', borderRadius: 8, border: 'none', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ maxHeight: 560, overflowY: 'auto' }}>
          {cargandoRecetas ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>Cargando...</div>
          ) : productosFiltrados.map((p, i) => {
            const tieneReceta = productosConReceta.has(p.nombre);
            const esActivo = productoSeleccionado === p.nombre;
            const numItems = recetas.filter(r => r.nombre_aliaddo === p.nombre).length;
            return (
              <div key={p.nombre} onClick={() => abrirProducto(p.nombre)}
                style={{
                  padding: '10px 16px', borderBottom: '1px solid #F3F4F6', cursor: 'pointer',
                  background: esActivo ? '#EFF6FF' : i % 2 === 0 ? 'white' : '#FAFAFA',
                  borderLeft: esActivo ? '3px solid #1D4ED8' : '3px solid transparent',
                  transition: 'background 0.1s',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: esActivo ? 700 : 500, color: '#111827', flex: 1 }}>
                    {p.nombre}
                  </div>
                  {tieneReceta ? (
                    <span style={{ fontSize: 11, background: '#D1FAE5', color: '#065F46', borderRadius: 10, padding: '2px 7px', fontWeight: 700, flexShrink: 0 }}>
                      {numItems} item{numItems > 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, background: '#F3F4F6', color: '#9CA3AF', borderRadius: 10, padding: '2px 7px', fontWeight: 600, flexShrink: 0 }}>
                      Sin receta
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Panel derecho: receta del producto seleccionado */}
      <div style={{ flex: 1 }}>
        {!productoSeleccionado ? (
          <div style={{ background: 'white', borderRadius: 14, padding: 48, textAlign: 'center', color: '#9CA3AF', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>👈</div>
            <div style={{ fontWeight: 600 }}>Selecciona un producto para ver o editar su receta</div>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            {/* Header */}
            <div style={{ background: '#1a1a2e', padding: '14px 20px' }}>
              <div style={{ color: 'white', fontWeight: 800, fontSize: 15 }}>📋 {productoSeleccionado}</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 2 }}>
                Define qué items de inventario descuenta este producto al venderse
              </div>
            </div>

            {/* Recetas existentes */}
            {recetasDelProducto.length > 0 && (
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 10 }}>
                  Items asignados
                </div>
                {recetasDelProducto.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F9FAFB' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{r.item_nombre}</span>
                      <span style={{ color: '#9CA3AF', fontSize: 12 }}> ({r.unidad_medida})</span>
                    </div>
                    <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 15, color: '#1D4ED8', minWidth: 40, textAlign: 'right' }}>
                      ×{r.cantidad}
                    </div>
                    <button onClick={() => onEliminar(r.id)}
                      style={{ padding: '4px 10px', border: '1px solid #FECACA', borderRadius: 6, background: '#FEF2F2', color: '#991B1B', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Formulario agregar items */}
            <div style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 12 }}>
                Agregar items
              </div>
              {itemsReceta.map((fila, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center' }}>
                  <select
                    value={fila.item_id}
                    onChange={e => handleCambiarFila(idx, 'item_id', e.target.value)}
                    style={{ flex: 2, padding: '9px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' }}>
                    <option value="">Seleccionar item...</option>
                    {itemsInv
                      .filter(i => !idsAsignados.has(i.id) || fila.item_id == i.id)
                      .map(i => <option key={i.id} value={i.id}>{i.nombre} ({i.unidad_medida})</option>)}
                  </select>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 13, color: '#6B7280', fontWeight: 600 }}>×</span>
                    <input
                      type="number" min="0.01" step="0.01"
                      value={fila.cantidad}
                      onChange={e => handleCambiarFila(idx, 'cantidad', e.target.value)}
                      style={{ width: 70, padding: '9px 10px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', textAlign: 'center', fontWeight: 700 }}
                    />
                  </div>
                  {itemsReceta.length > 1 && (
                    <button onClick={() => handleEliminarFila(idx)}
                      style={{ padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 14, color: '#6B7280' }}>
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button onClick={handleAgregarFila}
                  style={{ padding: '8px 16px', border: '2px dashed #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 13, color: '#6B7280', fontWeight: 600 }}>
                  + Agregar otro item
                </button>
                <button onClick={handleGuardarItems} disabled={guardando}
                  style={{ padding: '9px 22px', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#1a1a2e,#16213e)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  {guardando ? '⏳ Guardando...' : '💾 Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
